/**
 * Repairs rows whose ciphertext and scope have drifted apart.
 *
 * A sealed row can only be read by the members of the scope whose key sealed
 * it. Moving a row to a different scope does not change its ciphertext, so
 * after a move it stays readable by the scope it came FROM — which is how a
 * card shared into a household scope ends up invisible to the household: it
 * was sealed under the owner's personal scope, and no other member holds that
 * key. Two things create the drift:
 *
 *   * a per-account scope override (POST /api/set_account_scope), which
 *     re-homes the account and relocates its historical expenses;
 *   * account metadata written before the seal side keyed off the effective
 *     scope — it always used the Plaid item's scope, override or not.
 *
 * The server cannot repair either: re-sealing means opening the payload, and
 * only a member's browser holds a private key. So the shape is the same
 * two-phase exchange the location backfill uses — GET the candidate
 * ciphertexts, open each with the keys we hold, re-seal under the scope the
 * row lives in NOW, PUT the results.
 *
 * Idempotent and cheap to re-run: `resealForScope` reports `ok` for a row
 * that already opens under its own scope key, and rows that report `ok` are
 * never written. A sweep with nothing to fix sends no PUT at all.
 */
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useEncryption } from '@/composables/useEncryption'
import { invalidateFinancialData } from '@/utils/cacheUtils'
import { authenticatedGet, authenticatedPut, type AuthContext } from '@/utils/api'

interface SealedCandidate {
  scope_id: number
  encrypted_payload: string
}
interface AccountCandidate extends SealedCandidate {
  account_id: string
}
interface ExpenseCandidate extends SealedCandidate {
  expense_id: number
}

export interface ResealResult {
  /** Rows re-sealed under the scope they now live in. */
  accounts: number
  expenses: number
  /** Rows no key of ours opens — another member has to run this. */
  unreadable: number
}

/** Guard against a runaway loop if the server keeps handing back a cursor. */
const MAX_EXPENSE_PAGES = 200

export interface ResealOptions {
  /** Sweep this Plaid account's expenses too. Pass it right after a scope
   *  move — that move is the only thing that strands expenses. */
  accountId?: string | null
  /** Sweep EVERY accessible sealed expense. The manual repair; walks the
   *  whole ledger a page at a time, so it is not for the automatic path. */
  allExpenses?: boolean
}

export function useResealMutation(auth: AuthContext) {
  const queryClient = useQueryClient()
  const enc = useEncryption()

  return useMutation({
    mutationFn: async (opts: ResealOptions = {}): Promise<ResealResult> => {
      const result: ResealResult = { accounts: 0, expenses: 0, unreadable: 0 }
      if (!enc.isUnlocked.value) return result

      let cursor: number | null = null
      let page = 0
      let sweepExpenses = !!(opts.accountId || opts.allExpenses)

      do {
        const params = new URLSearchParams()
        if (opts.accountId) params.set('account_id', opts.accountId)
        if (opts.allExpenses) params.set('all_expenses', '1')
        if (cursor) params.set('cursor', String(cursor))
        const qs = params.toString()

        const response = await authenticatedGet(
          `/api/encryption/reseal${qs ? `?${qs}` : ''}`,
          auth,
        )
        if (!response.ok) throw new Error(`${response.status}: ${await response.text()}`)
        const data = await response.json()
        if (!data.success) throw new Error(data.error || 'Re-seal scan failed')

        const accounts: Record<string, string> = {}
        const expenses: Record<string, string> = {}

        // Accounts come back on every page; only act on the first one so a
        // multi-page expense sweep doesn't re-test them each time.
        if (page === 0) {
          for (const row of (data.accounts || []) as AccountCandidate[]) {
            const outcome = await enc.resealForScope(row.encrypted_payload, row.scope_id)
            if (outcome.status === 'resealed') accounts[row.account_id] = outcome.payload
            else if (outcome.status === 'unreadable') result.unreadable += 1
          }
        }

        for (const row of (data.expenses || []) as ExpenseCandidate[]) {
          const outcome = await enc.resealForScope(row.encrypted_payload, row.scope_id)
          if (outcome.status === 'resealed') expenses[String(row.expense_id)] = outcome.payload
          else if (outcome.status === 'unreadable') result.unreadable += 1
        }

        if (Object.keys(accounts).length || Object.keys(expenses).length) {
          const put = await authenticatedPut(
            '/api/encryption/reseal',
            { accounts, expenses },
            auth,
          )
          if (!put.ok) throw new Error(`${put.status}: ${await put.text()}`)
          const putData = await put.json()
          if (!putData.success) throw new Error(putData.error || 'Failed to store re-seal')
          result.accounts += Number(putData.accounts_updated || 0)
          result.expenses += Number(putData.expenses_updated || 0)
        }

        cursor = sweepExpenses ? (data.next_cursor ?? null) : null
        page += 1
        if (page >= MAX_EXPENSE_PAGES) {
          console.warn('[reseal] stopped after', page, 'pages')
          sweepExpenses = false
          cursor = null
        }
      } while (cursor)

      return result
    },
    onSuccess: (result) => {
      // Only bust caches when something actually changed — this runs
      // unattended, and a no-op sweep should cost the UI nothing.
      if (result.accounts || result.expenses) invalidateFinancialData(queryClient)
    },
    onError: (error) => {
      console.error('Failed to re-seal scope-drifted rows:', error)
    },
  })
}
